import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import DateRangeFilter from './DateRangeFilter';

const CreateAuditModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [percentage, setPercentage] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [assistants, setAssistants] = useState([]);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(true);

  // Fetch assistants on mount
  React.useEffect(() => {
    const fetchAssistants = async () => {
      setIsLoadingAssistants(true);
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (!user) {
          setError('Not authenticated. Please log in.');
          return;
        }

        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        if (membershipsError) throw membershipsError;
        if (!memberships || memberships.length === 0) {
          setError('No organization found.');
          return;
        }

        const { data: assistantsData, error: assistantsError } = await supabase
          .from('organization_assistants')
          .select('assistant_id')
          .eq('organization_id', memberships[0].organization_id);

        if (assistantsError) throw assistantsError;

        const assistantsList = assistantsData.map(a => a.assistant_id);
        setAssistants(assistantsList);
        if (assistantsList.length > 0) {
          setSelectedAssistant(assistantsList[0]);
        }
      } catch (error) {
        console.error('Error fetching assistants:', error);
        setError('Failed to fetch assistants: ' + error.message);
      } finally {
        setIsLoadingAssistants(false);
      }
    };

    if (isOpen) {
      fetchAssistants();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!startDate || !endDate) {
      setError('Please select a date range.');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be after start date.');
      return;
    }

    const percentageNum = parseFloat(percentage);
    if (isNaN(percentageNum) || percentageNum < 0 || percentageNum > 100) {
      setError('Percentage must be between 0 and 100.');
      return;
    }

    if (!selectedAssistant) {
      setError('Please select an assistant.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get current user and organization
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated.');

      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id);

      if (membershipsError) throw membershipsError;
      if (!memberships || memberships.length === 0) {
        throw new Error('No organization found.');
      }

      const organizationId = memberships[0].organization_id;

      // Build date range for query
      const startDateISO = new Date(startDate);
      startDateISO.setHours(0, 0, 0, 0);
      
      const endDateISO = new Date(endDate);
      endDateISO.setHours(23, 59, 59, 999);

      // Fetch all calls in date range
      const { data: allCalls, error: allCallsError } = await supabase
        .from('calls')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('assistant_id', selectedAssistant)
        .gte('created_at_vapi', startDateISO.toISOString())
        .lte('created_at_vapi', endDateISO.toISOString());

      if (allCallsError) throw allCallsError;

      const totalCalls = allCalls?.length || 0;
      const selectedCount = Math.ceil(totalCalls * percentageNum / 100);

      if (totalCalls === 0) {
        setError('No calls found in the selected date range.');
        setIsSubmitting(false);
        return;
      }

      // Shuffle and select random calls
      const shuffled = [...(allCalls || [])].sort(() => 0.5 - Math.random());
      const selectedCalls = shuffled.slice(0, selectedCount);

      // Create audit record
      const { data: auditData, error: auditError } = await supabase
        .from('audits')
        .insert({
          organization_id: organizationId,
          created_by: user.id,
          name: name || null,
          percentage: percentageNum,
          start_date: startDateISO.toISOString(),
          end_date: endDateISO.toISOString(),
          total_calls_in_range: totalCalls,
          selected_calls_count: selectedCount,
          status: 'in_progress'
        })
        .select()
        .single();

      if (auditError) throw auditError;

      // Create audit_selected_calls records
      if (selectedCalls.length > 0) {
        const selectedCallsData = selectedCalls.map(call => ({
          audit_id: auditData.id,
          call_id: call.id
        }));

        const { error: selectedCallsError } = await supabase
          .from('audit_selected_calls')
          .insert(selectedCallsData);

        if (selectedCallsError) throw selectedCallsError;
      }

      // Close modal and navigate to audit details
      onClose();
      navigate(`/dashboard/audits/${auditData.id}`);

    } catch (e) {
      console.error('Failed to create audit:', e);
      setError(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-xl max-w-lg w-full mx-4 transform transition-all duration-300 ease-in-out scale-100">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create New Audit</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Audit Name (Optional)
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark-2 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Q4 2024 Audit"
            />
          </div>

          <div>
            <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date Range
            </label>
            <DateRangeFilter
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </div>

          {!isLoadingAssistants && assistants.length > 0 && (
            <div>
              <label htmlFor="assistant" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assistant
              </label>
              <select
                id="assistant"
                value={selectedAssistant || ''}
                onChange={(e) => setSelectedAssistant(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark-2 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                {assistants.map((assistantId) => (
                  <option key={assistantId} value={assistantId}>
                    Assistant {assistantId.substring(0, 8)}...
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="percentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Percentage (%)
            </label>
            <input
              type="number"
              id="percentage"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              min="0"
              max="100"
              step="0.1"
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark-2 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., 10"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Percentage of calls to randomly select for audit (0-100)
            </p>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoadingAssistants}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : 'Create Audit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAuditModal;

